// Localização: frontend/src/components/AgendaTarefas/TarefaProfissionalFilter.jsx
// Componente de Filtro de Profissional para Tarefas com Dropdown

import React, { useState, useEffect } from 'react';
import { User, Check, X } from 'lucide-react';
import api from '../../api/api';

/**
 * @component TarefaProfissionalFilter
 * @description Componente de filtro de profissional para tarefas com dropdown
 * @param {Array} profissionais - Array de IDs de profissionais selecionados
 * @param {function} onProfissionaisChange - Callback quando profissionais mudam
 * @param {function} onClose - Callback para fechar o filtro (opcional)
 * @param {string} className - Classes CSS adicionais
 */
const TarefaProfissionalFilter = ({
    profissionais = [],
    onProfissionaisChange,
    onClose = null,
    className = ''
}) => {
    const [profissionaisList, setProfissionaisList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const carregarProfissionais = async () => {
            setIsLoading(true);
            try {
                const response = await api.get('/usuarios');
                // Filtrar apenas profissionais (roles que podem ter tarefas)
                const profs = (response.data || []).filter(u => 
                    ['enfermeiro', 'medico', 'fisioterapeuta', 'cuidador', 'gerente', 'admin', 'proprietario'].includes(
                        u.role?.toLowerCase()
                    )
                );
                setProfissionaisList(profs);
            } catch (err) {
                console.error('Erro ao carregar profissionais:', err);
            } finally {
                setIsLoading(false);
            }
        };

        carregarProfissionais();
    }, []);

    const handleProfissionalToggle = (profId) => {
        if (!onProfissionaisChange) return;
        
        const currentProfissionais = profissionais || [];
        const profIdStr = profId.toString();
        let newProfissionais;
        if (currentProfissionais.includes(profIdStr)) {
            newProfissionais = currentProfissionais.filter(p => p !== profIdStr);
        } else {
            newProfissionais = [...currentProfissionais, profIdStr];
        }
        onProfissionaisChange(newProfissionais);
    };

    return (
        <div className={`bg-white rounded-xl shadow-2xl border border-gray-200 py-2 relative ${className}`}>
            {/* Botão de Fechar */}
            {onClose && (
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 z-10"
                    title="Fechar filtro"
                    aria-label="Fechar filtro"
                >
                    <X className="h-4 w-4" />
                </button>
            )}

            {/* Header */}
            <div className="px-4 py-2 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <User className="h-4 w-4 text-emerald-600" />
                    Profissional
                </h3>
            </div>

            {/* Conteúdo do dropdown */}
            <div className="pt-1 max-h-[400px] overflow-y-auto">
                {isLoading ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                        Carregando profissionais...
                    </div>
                ) : profissionaisList.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                        Nenhum profissional encontrado
                    </div>
                ) : (
                    profissionaisList.map(prof => {
                        const isSelected = profissionais?.includes(prof.id.toString());
                        return (
                            <button
                                key={prof.id}
                                onClick={() => handleProfissionalToggle(prof.id)}
                                className={`
                                    w-full flex items-center gap-3 px-4 py-2.5
                                    transition-colors text-left
                                    ${isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'}
                                `}
                            >
                                <div className={`
                                    flex items-center justify-center w-5 h-5 rounded border-2 transition-colors
                                    ${isSelected 
                                        ? 'bg-emerald-600 border-emerald-600' 
                                        : 'border-gray-300 bg-white'
                                    }
                                `}>
                                    {isSelected && (
                                        <Check className="h-3 w-3 text-white" />
                                    )}
                                </div>
                                <div className="flex-1 text-left">
                                    <span className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-gray-700'}`}>
                                        {prof.nome_completo}
                                    </span>
                                    {prof.role && (
                                        <span className={`text-xs ml-2 ${isSelected ? 'text-emerald-600' : 'text-gray-500'}`}>
                                            ({prof.role})
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default TarefaProfissionalFilter;

